// API configuration and utilities
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';
const DEBUG_API = process.env.REACT_APP_DEBUG_API === 'true';
const LOCAL_API_FALLBACK = process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:3002';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

const debugLog = (...args: any[]) => {
  if (DEBUG_API) {
    console.log(...args);
  }
};

class ApiService {
  private baseURL: string;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  private shouldTryLocalFallback(): boolean {
    return /trycloudflare\.com/i.test(this.baseURL);
  }

  private async fetchWithBase(
    baseURL: string,
    endpoint: string,
    options: RequestInit,
    token: string,
  ): Promise<Response> {
    const url = `${baseURL}${endpoint}`;
    debugLog(`API Request: ${options.method || 'GET'} ${url}`);
    return fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    });
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    debugLog('API Request options:', options);

    // Get auth token - for admin endpoints, use the test token
    let token = '';
    const storedUser = localStorage.getItem('adminUser');
    if (storedUser) {
      try {
        JSON.parse(storedUser);
        // Use the test admin token for authenticated requests
        token = 'admin-token';
      } catch (error) {
        console.error('Error parsing stored user:', error);
      }
    }

    try {
      let activeBaseURL = this.baseURL;
      let response = await this.fetchWithBase(activeBaseURL, endpoint, options, token);

      if (!response.ok && [502, 503, 504].includes(response.status) && this.shouldTryLocalFallback()) {
        debugLog(`Primary API returned ${response.status}. Retrying with local fallback: ${LOCAL_API_FALLBACK}`);
        activeBaseURL = LOCAL_API_FALLBACK;
        response = await this.fetchWithBase(activeBaseURL, endpoint, options, token);
      }

      debugLog(`API Response status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`API Error response: ${errorText}`);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the response text first to see what we're dealing with
      const responseText = await response.text();
      debugLog('API Raw response (first 200 chars):', responseText.substring(0, 200));

      try {
        const data = JSON.parse(responseText);
        debugLog('API Response data:', data);
        return { success: true, data };
      } catch (parseError) {
        console.error('JSON Parse Error:', parseError);
        console.error('Response was not valid JSON. Full response:', responseText);
        throw new Error('Invalid JSON response from server');
      }
    } catch (error) {
      if (this.shouldTryLocalFallback()) {
        try {
          debugLog(`API request failed on primary base URL. Retrying with local fallback: ${LOCAL_API_FALLBACK}`);
          const fallbackResponse = await this.fetchWithBase(LOCAL_API_FALLBACK, endpoint, options, token);

          if (fallbackResponse.ok) {
            const responseText = await fallbackResponse.text();
            const data = JSON.parse(responseText);
            return { success: true, data };
          }
        } catch (fallbackError) {
          debugLog('Fallback API request also failed:', fallbackError);
        }
      }

      console.error('API Request failed:', error);
      console.error('API Error details:', {
        url: `${this.baseURL}${endpoint}`,
        method: options.method || 'GET',
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      return {
        success: false,
        error: {
          code: 'API_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async patch<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const apiService = new ApiService();

// Create axios-like interface for backward compatibility
const api = {
  get: async (url: string) => {
    const response = await apiService.get(url);
    return { data: response.data };
  },
  post: async (url: string, data?: any) => {
    const response = await apiService.post(url, data);
    return { data: response.data };
  },
  put: async (url: string, data?: any) => {
    const response = await apiService.put(url, data);
    return { data: response.data };
  },
  patch: async (url: string, data?: any) => {
    const response = await apiService.patch(url, data);
    return { data: response.data };
  },
  delete: async (url: string) => {
    const response = await apiService.delete(url);
    return { data: response.data };
  },
};

export default api;
